import React, { useLayoutEffect, useMemo, useRef } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Divider,
  Grid,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material'
import { Assignment, DragIndicator, DriveFileMoveOutlined } from '@mui/icons-material'
import { Droppable, Draggable } from 'react-beautiful-dnd'
import moment from 'moment'
import { CostSourceText } from '../CostSourceText'
import type { QuoteCardProps } from './types'
import {
  LINE_ITEM_TYPE_NAMES,
  LINE_ITEM_TYPE_ORDER,
  STEP1_PT_GRID_COLUMNS,
  STEP1_STANDARD_GRID_COLUMNS,
  SCROLLABLE_MAX_HEIGHT,
  SCROLL_CONTAINER_MIN_HEIGHT,
} from './constants'
import { formatCurrency, groupLineItemsByType } from './utils'

const QuoteCard = React.memo(function QuoteCard({
  quote,
  droppableId,
  onToggleItem,
  onMoveAllOfType,
  onMoveAll,
}: QuoteCardProps) {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null)
  const scrollTopRef = useRef(0)

  useLayoutEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = scrollTopRef.current
    }
  })

  const groupedItems = useMemo(
    () => groupLineItemsByType(quote.lineItems),
    [quote.lineItems]
  )
  const hasMovableItems = useMemo(
    () => quote.lineItems.some((item) => !item.originalQuoteId),
    [quote.lineItems]
  )

  return (
    <Card elevation={3}>
      <CardHeader
        title={quote.title}
        avatar={<Assignment color="primary" />}
        action={
          <Button
            size="small"
            variant="outlined"
            onClick={() => onMoveAll(droppableId as 'original' | 'consumer')}
            disabled={
              droppableId === 'original' ? !hasMovableItems : quote.lineItems.length === 0
            }
            sx={{
              mt: 1,
              mr: 1,
              '&.Mui-disabled': {
                color: 'text.disabled',
                borderColor: 'action.disabled',
                opacity: 1,
              },
            }}
          >
            {droppableId === 'original' ? 'Consume All' : 'Un-Consume'}
          </Button>
        }
      />
      <CardContent>
        <Box sx={{ mb: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={6} md={2}>
              <Typography variant="caption" color="text.secondary">
                <strong>Type:</strong>
              </Typography>
              <Typography variant="body2">{quote.type}</Typography>
            </Grid>
            <Grid item xs={6} sm={2}>
              <Typography variant="caption" color="text.secondary">
                <strong>Start Date:</strong>
              </Typography>
              <Typography variant="body2">
                {moment(quote.startDate).format('L')}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={2}>
              <Typography variant="caption" color="text.secondary">
                <strong># of Days:</strong>
              </Typography>
              <Typography variant="body2">{quote.bundleDurationDays}</Typography>
            </Grid>
            <Grid item xs={6} sm={2}>
              <Typography variant="caption" color="text.secondary">
                <strong>End Date:</strong>
              </Typography>
              <Typography variant="body2">{moment(quote.endDate).format('L')}</Typography>
            </Grid>
            <Grid item xs={6} sm={2}>
              <Typography variant="caption" color="text.secondary">
                <strong>Group #:</strong>
              </Typography>
              <Typography variant="body2">{quote.groupNumber}</Typography>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" gutterBottom>
          Line Items ({quote.lineItems.length})
        </Typography>

        <Droppable droppableId={droppableId}>
          {(provided, snapshot) => (
            <Box
              ref={scrollContainerRef}
              onScroll={(e: React.UIEvent<HTMLDivElement>) => {
                scrollTopRef.current = e.currentTarget.scrollTop
              }}
              sx={{
                minHeight: SCROLL_CONTAINER_MIN_HEIGHT,
                maxHeight: SCROLLABLE_MAX_HEIGHT,
                overflow: 'auto',
                bgcolor: snapshot.isDraggingOver ? 'action.hover' : 'background.paper',
                border: '2px dashed',
                borderColor: snapshot.isDraggingOver ? 'primary.main' : 'divider',
                borderRadius: 1,
                p: 1,
              }}
            >
              <Box ref={provided.innerRef} {...provided.droppableProps}>
                {(() => {
                  let globalIndex = 0
                  return LINE_ITEM_TYPE_ORDER.map((type) => {
                    const items = groupedItems[type]
                    const hasMovableItemsOfType = items.some(
                      (item) => !item.originalQuoteId
                    )

                    return (
                      <Box key={type} sx={{ mb: 2 }}>
                        <Box
                          sx={{
                            bgcolor: 'primary.main',
                            color: 'primary.contrastText',
                            p: 1,
                            borderRadius: 1,
                            mb: 1,
                            fontWeight: 'bold',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                            {LINE_ITEM_TYPE_NAMES[type]}
                          </Typography>
                          {items.length > 0 && (
                            <Button
                              size="small"
                              variant="text"
                              disabled={
                                droppableId === 'original'
                                  ? !hasMovableItemsOfType
                                  : items.length === 0
                              }
                              sx={{
                                color: 'primary.contrastText',
                                p: '2px 6px',
                                minWidth: 'auto',
                                fontSize: '0.7rem',
                                lineHeight: 1.2,
                                border: '1px solid rgba(255,255,255,0.5)',
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' },
                                '&.Mui-disabled': {
                                  color: 'rgba(255,255,255,0.72)',
                                  WebkitTextFillColor: 'rgba(255,255,255,0.72)',
                                  borderColor: 'rgba(255,255,255,0.35)',
                                  opacity: 1,
                                },
                              }}
                              onClick={() =>
                                onMoveAllOfType(
                                  type,
                                  droppableId as 'original' | 'consumer'
                                )
                              }
                            >
                              {droppableId === 'original' ? 'Consume All' : 'Un-Consume'}
                            </Button>
                          )}
                        </Box>
                        {items.length === 0 ? (
                          <Box
                            sx={{
                              p: 2,
                              textAlign: 'center',
                              color: 'text.secondary',
                              bgcolor: 'action.hover',
                              borderRadius: 1,
                              border: '1px dashed',
                              borderColor: 'divider',
                            }}
                          >
                            <Typography variant="caption">
                              {droppableId === 'consumer'
                                ? 'Drag items here'
                                : 'No items'}
                            </Typography>
                          </Box>
                        ) : (
                          <>
                            <Box
                              sx={{
                                display: 'grid',
                                gridTemplateColumns:
                                  type === 'PT'
                                    ? STEP1_PT_GRID_COLUMNS
                                    : STEP1_STANDARD_GRID_COLUMNS,
                                gap: 1,
                                px: 2,
                                py: 1,
                                bgcolor: 'background.default',
                                borderBottom: '2px solid',
                                borderColor: 'primary.light',
                                mb: 1,
                                alignItems: 'center',
                                width: 'fit-content',
                                minWidth: '100%',
                              }}
                            >
                              <Box />
                              <Box />
                              {type === 'PT' ? (
                                <>
                                  <Typography
                                    variant="caption"
                                    fontWeight="bold"
                                    sx={{
                                      borderRight: '1px solid',
                                      borderColor: 'divider',
                                      pr: 1,
                                    }}
                                  >
                                    Provider
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    fontWeight="bold"
                                    sx={{
                                      textAlign: 'right',
                                      borderRight: '1px solid',
                                      borderColor: 'divider',
                                      pr: 1,
                                    }}
                                  >
                                    # of Visits
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    fontWeight="bold"
                                    sx={{
                                      textAlign: 'right',
                                      borderRight: '1px solid',
                                      borderColor: 'divider',
                                      pr: 1,
                                    }}
                                  >
                                    Est. U&C/Visit
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    fontWeight="bold"
                                    sx={{
                                      textAlign: 'right',
                                      borderRight: '1px solid',
                                      borderColor: 'divider',
                                      pr: 1,
                                    }}
                                  >
                                    Est. U&C $
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    fontWeight="bold"
                                    sx={{
                                      textAlign: 'right',
                                      borderRight: '1px solid',
                                      borderColor: 'divider',
                                      pr: 1,
                                    }}
                                  >
                                    Quoted/Visit
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    fontWeight="bold"
                                    sx={{
                                      textAlign: 'right',
                                      borderRight: '1px solid',
                                      borderColor: 'divider',
                                      pr: 1,
                                    }}
                                  >
                                    Quoted $
                                  </Typography>
                                </>
                              ) : (
                                <>
                                  <Typography
                                    variant="caption"
                                    fontWeight="bold"
                                    sx={{
                                      borderRight: '1px solid',
                                      borderColor: 'divider',
                                      pr: 1,
                                    }}
                                  >
                                    Provider
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    fontWeight="bold"
                                    sx={{
                                      borderRight: '1px solid',
                                      borderColor: 'divider',
                                      pr: 1,
                                    }}
                                  >
                                    Physician
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    fontWeight="bold"
                                    sx={{
                                      borderRight: '1px solid',
                                      borderColor: 'divider',
                                      pr: 1,
                                    }}
                                  >
                                    CPT
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    fontWeight="bold"
                                    sx={{
                                      borderRight: '1px solid',
                                      borderColor: 'divider',
                                      pr: 1,
                                    }}
                                  >
                                    Description
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    fontWeight="bold"
                                    sx={{
                                      borderRight: '1px solid',
                                      borderColor: 'divider',
                                      pr: 1,
                                    }}
                                  >
                                    Cost Source
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    fontWeight="bold"
                                    sx={{
                                      textAlign: 'right',
                                      borderRight: '1px solid',
                                      borderColor: 'divider',
                                      pr: 1,
                                    }}
                                  >
                                    Charges $
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    fontWeight="bold"
                                    sx={{
                                      textAlign: 'right',
                                      pr: 1,
                                    }}
                                  >
                                    Quoted $
                                  </Typography>
                                </>
                              )}
                            </Box>
                            {items.map((item) => {
                              const itemIndex = globalIndex++
                              const isDragLocked = !!item.originalQuoteId
                              return (
                                <Draggable
                                  key={item.id}
                                  draggableId={item.id}
                                  index={itemIndex}
                                  isDragDisabled={isDragLocked}
                                >
                                  {(provided, snapshot) => (
                                    <Paper
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      {...provided.dragHandleProps}
                                      elevation={snapshot.isDragging ? 8 : 1}
                                      sx={{
                                        mb: 1,
                                        cursor: isDragLocked ? 'not-allowed' : 'grab',
                                        bgcolor: snapshot.isDragging
                                          ? 'action.selected'
                                          : 'background.paper',
                                        '&:hover': { bgcolor: 'action.hover' },
                                        width: 'fit-content',
                                        minWidth: '100%',
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          display: 'grid',
                                          gridTemplateColumns:
                                            item.type === 'PT'
                                              ? STEP1_PT_GRID_COLUMNS
                                              : STEP1_STANDARD_GRID_COLUMNS,
                                          gap: 1,
                                          alignItems: 'center',
                                          p: 2,
                                        }}
                                      >
                                        <Checkbox
                                          checked={droppableId === 'consumer'}
                                          disabled={isDragLocked}
                                          onChange={(e) =>
                                            onToggleItem(
                                              item.id,
                                              droppableId as 'original' | 'consumer',
                                              e
                                            )
                                          }
                                          size="small"
                                        />
                                        {isDragLocked ? (
                                          <Tooltip
                                            title={`This line item has been consumed by quote ${
                                              item.movedToQuoteLegacyEOCGroupNumber ||
                                              '[UNKNOWN]'
                                            }.`}
                                          >
                                            <DriveFileMoveOutlined
                                              fontSize="small"
                                              color="error"
                                            />
                                          </Tooltip>
                                        ) : (
                                          <DragIndicator color="action" />
                                        )}
                                        {item.type === 'PT' ? (
                                          <>
                                            <Typography
                                              variant="caption"
                                              noWrap
                                              sx={{
                                                borderRight: '1px solid',
                                                borderColor: 'divider',
                                                pr: 1,
                                              }}
                                            >
                                              {item.providerGroup}
                                            </Typography>
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                textAlign: 'right',
                                                borderRight: '1px solid',
                                                borderColor: 'divider',
                                                pr: 1,
                                              }}
                                            >
                                              {item.numberOfVisits || 0}
                                            </Typography>
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                textAlign: 'right',
                                                borderRight: '1px solid',
                                                borderColor: 'divider',
                                                pr: 1,
                                              }}
                                            >
                                              ${formatCurrency(item.estUCPerVisit || 0)}
                                            </Typography>
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                textAlign: 'right',
                                                borderRight: '1px solid',
                                                borderColor: 'divider',
                                                pr: 1,
                                              }}
                                            >
                                              ${formatCurrency(item.estUCTotal || 0)}
                                            </Typography>
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                textAlign: 'right',
                                                borderRight: '1px solid',
                                                borderColor: 'divider',
                                                pr: 1,
                                              }}
                                            >
                                              ${formatCurrency(item.quotedPerVisit || 0)}
                                            </Typography>
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                textAlign: 'right',
                                              }}
                                            >
                                              ${formatCurrency(item.quotedAmount)}
                                            </Typography>
                                          </>
                                        ) : (
                                          <>
                                            <Typography
                                              variant="caption"
                                              noWrap
                                              title={item.providerGroup}
                                              sx={{
                                                borderRight: '1px solid',
                                                borderColor: 'divider',
                                                pr: 1,
                                              }}
                                            >
                                              {item.providerGroup}
                                            </Typography>
                                            <Typography
                                              variant="caption"
                                              noWrap
                                              title={item.providerType}
                                              sx={{
                                                borderRight: '1px solid',
                                                borderColor: 'divider',
                                                pr: 1,
                                              }}
                                            >
                                              {item.providerType || '-'}
                                            </Typography>
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                borderRight: '1px solid',
                                                borderColor: 'divider',
                                                pr: 1,
                                              }}
                                            >
                                              {item.cptCode || '-'}
                                            </Typography>
                                            <Typography
                                              variant="caption"
                                              noWrap
                                              title={item.description}
                                              sx={{
                                                borderRight: '1px solid',
                                                borderColor: 'divider',
                                                pr: 1,
                                              }}
                                            >
                                              {item.description || '-'}
                                            </Typography>
                                            <Typography
                                              variant="caption"
                                              noWrap
                                              sx={{
                                                borderRight: '1px solid',
                                                borderColor: 'divider',
                                                pr: 1,
                                              }}
                                            >
                                              {item.costSource !== undefined
                                                ? CostSourceText[item.costSource]
                                                : '-'}
                                            </Typography>
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                textAlign: 'right',
                                                borderRight: '1px solid',
                                                borderColor: 'divider',
                                                pr: 1,
                                              }}
                                            >
                                              ${formatCurrency(item.billedCharges)}
                                            </Typography>
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                textAlign: 'right',
                                                pr: 1,
                                              }}
                                            >
                                              ${formatCurrency(item.quotedAmount)}
                                            </Typography>
                                          </>
                                        )}
                                      </Box>
                                    </Paper>
                                  )}
                                </Draggable>
                              )
                            })}
                          </>
                        )}
                      </Box>
                    )
                  })
                })()}
                {provided.placeholder}
              </Box>
            </Box>
          )}
        </Droppable>

        <Divider sx={{ my: 2 }} />

        <Card
          elevation={0}
          sx={{
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            py: 0.75,
            px: 1.5,
          }}
        >
          <CardContent sx={{ py: 0.75, px: 0, '&:last-child': { pb: 0.75 } }}>
            <Grid container spacing={6}>
              <Grid item xs={4}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {droppableId === 'consumer'
                      ? 'Original Quoted Cost:'
                      : 'Total Quoted Cost:'}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 'bold' }}
                    color="text.primary"
                  >
                    ${formatCurrency(quote.totalOriginalCost)}
                  </Typography>
                </Box>
              </Grid>
              {droppableId === 'consumer' && (
                <>
                  <Grid item xs={4}>
                    <Box
                      sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Consumed Cost:
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 'bold' }}
                        color="text.primary"
                      >
                        ${formatCurrency(quote.consumedCost)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box
                      sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Total After Consumed:
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 'bold' }}
                        color="text.primary"
                      >
                        ${formatCurrency(quote.consumedCost + quote.totalOriginalCost)}
                      </Typography>
                    </Box>
                  </Grid>
                </>
              )}
            </Grid>
          </CardContent>
        </Card>
      </CardContent>
    </Card>
  )
})

export default QuoteCard
